/**
 * Onboarding seen-state (spec 020 US2) — remembers which sequenced-onboarding
 * flows a user has finished/skipped so they never reappear. Per-browser via
 * localStorage (same convention as stores/surface.ts), with an in-memory
 * once-per-session fallback when storage is unavailable (private mode) so it
 * never throws. Keyed by a stable sequence id (e.g. "dashboard", "editor").
 */
const KEY = "genz.onboarding";

// Survives within a session even when localStorage is blocked, so a skipped
// sequence does not flicker back on the same visit.
let sessionSeen = new Set<string>();

function readSeen(): Set<string> {
  const set = new Set(sessionSeen);
  try {
    const raw = localStorage.getItem(KEY);
    const parsed = raw ? (JSON.parse(raw) as unknown) : [];
    if (Array.isArray(parsed)) for (const k of parsed) if (typeof k === "string") set.add(k);
  } catch {
    /* storage blocked — session memory only */
  }
  return set;
}

export function hasSeenOnboarding(key: string): boolean {
  return readSeen().has(key);
}

export function markOnboardingSeen(key: string): void {
  sessionSeen.add(key);
  try {
    const set = readSeen();
    localStorage.setItem(KEY, JSON.stringify([...set]));
  } catch {
    /* ignore (private mode) — sessionSeen still suppresses re-show this session */
  }
}

/** Clear remembered onboarding state (dev / "reset tips" / test isolation). */
export function clearOnboarding(): void {
  sessionSeen = new Set();
  try { localStorage.removeItem(KEY); } catch { /* ignore */ }
}
